import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { FC, useCallback, useState } from "react";
import {
  getNftDataFromSolsea,
  refreshSolseaDataAfterListing,
} from "../../../../../../../api/solsea.api";

import { MESSAGE_TYPE } from "../../../../../../../common/constants/common.constants";
import {
  ProposalAction,
  ProposalTypeForProgram,
} from "../../../../../../../common/enums/proposals.enums";
import { IClubDataForProposal } from "../../../../../../../common/interfaces/clubs.interface";
import {
  IBasicProposalData,
  IProposalWithMetaData,
} from "../../../../../../../common/interfaces/proposals.interface";
import { dashboardStore } from "../../../../../../../contexts/dashboardStore";
import { vaultStore } from "../../../../../../../contexts/vaultStore";
import { executeSolseaProposal } from "../../../../../../../solana/common/helpers/solsea.helpers";
import {
  getClubVault,
  getGovernaceAccountsForRealm,
  getTreasuryForClub,
  refreshRoleConfig,
} from "../../../../../../../solana/methods/clubs";
import {
  executeMagicEdenBuyProposal,
  executeMagicEdenSell,
  executeTransferProposalTx,
  executeVaultTreasuryRelatedProposal,
  executeWithdrawalProposal,
} from "../../../../../../../solana/methods/proposals";
import { createNotification } from "../../../../../../../utilities/notification";
import SubmitButton from "../../../../../../SubmitButton/SubmitButton";
import { clubsStore } from "../../../../../../../contexts/clubStore";
import {
  executeAddSellPermissionProposal,
  executeUpdateRoleWeight,
  executeUpdateGovConfig,
} from "../../../../../../../solana/common/helpers/change-club-config.helpers";

const ExecuteButton: FC<{
  proposalWithMetaData: IProposalWithMetaData;
  clubBasicInfoProposal: IClubDataForProposal;
  updateProposalWithMetaData: (proposal: IBasicProposalData) => void;
}> = ({
  proposalWithMetaData,
  clubBasicInfoProposal,
  updateProposalWithMetaData,
}) => {
  const wallet = useAnchorWallet();

  const { withdrawals, setWithdrawals } = dashboardStore();
  const {
    setTreasury,
    withdrawalMembersInfo,
    setWithdrawalMembersInfo,
    clubBasicInfo,
    updateClubBasicInfo,
    setVault,
    treasury,
  } = clubsStore();

  const { setAllNfts } = vaultStore();

  const [submitting, toggleSubmitting] = useState(false);
  const [isExecuteDisabled, toggleIsExecuteDisabled] = useState(false);

  const executeWithdrawal = useCallback(async () => {
    try {
      toggleSubmitting(true);
      await executeWithdrawalProposal(
        proposalWithMetaData!,
        clubBasicInfoProposal.clubName,
        clubBasicInfoProposal.treasury,
        wallet!,
        clubBasicInfoProposal.withdrawalGovernance
      );
      if (!clubBasicInfoProposal.isDashboardProposal) {
        const updatedTreasury = await getTreasuryForClub(
          clubBasicInfoProposal.clubName
        );
        setTreasury(updatedTreasury);
      } else {
        const dashboardWithdrawals = [...withdrawals!];
        dashboardWithdrawals.unshift({
          club: {
            clubAddress: clubBasicInfoProposal.address,
            clubImageUrl: clubBasicInfoProposal.clubImageUrl!,
            clubName: clubBasicInfoProposal.clubName,
            communityMint: clubBasicInfoProposal.mint,
            realmAddress: clubBasicInfoProposal.realmAddress!,
            stakeCount: clubBasicInfoProposal.stakeCount!,
            type: clubBasicInfoProposal.clubType,
            numberOfMembers: clubBasicInfoProposal.numberOfMembers!,
            roleConfig: clubBasicInfoProposal.roleConfig,
          },
          fundraiseCount:
            proposalWithMetaData.proposalBasicData!.proposalMetadata
              .currentFundraiseIndex,
          hasWithdrawn:
            proposalWithMetaData!.metadata.withdrawalMembersInfo.hasUserClaimed,
          relatedProposal:
            proposalWithMetaData.proposalBasicData!.proposalAccount.pubkey,
          totalFinancialPower:
            proposalWithMetaData?.metadata.totalFinancialPower,
          withdrawal: proposalWithMetaData!.metadata.withdrawal,
          withdrawalDataAddress: proposalWithMetaData!.metadata.withdrawalData,
          withdrawalRecordAmount:
            proposalWithMetaData!.metadata.withdrawalMembersInfo.withdrawAmount,
          totalWithdraw:
            proposalWithMetaData!.metadata.withdrawalAmount / LAMPORTS_PER_SOL,
          withdrawalMint:
            proposalWithMetaData!.metadata.withdrawalMembersInfo.withdrawalMint,
          withdrawalMintDecimals:
            proposalWithMetaData!.metadata.withdrawalMembersInfo
              .withdrawalMintDecimals,
          withdrawalMintSymbol:
            proposalWithMetaData!.metadata.withdrawalMembersInfo
              .withdrawalMintSymbol,
          treasury: clubBasicInfoProposal.treasury,
          proposalCreatedAt:
            proposalWithMetaData.proposalBasicData.proposalMetadata.createdAt,
        });
        setWithdrawals(dashboardWithdrawals);
      }

      updateProposalWithMetaData!(proposalWithMetaData.proposalBasicData);
      if (!clubBasicInfoProposal.isDashboardProposal && proposalWithMetaData) {
        const updatedWithdrawalMembersInfo = withdrawalMembersInfo
          ? [
              ...withdrawalMembersInfo,
              proposalWithMetaData.metadata.withdrawalMembersInfo,
            ]
          : [proposalWithMetaData.metadata.withdrawalMembersInfo];
        setWithdrawalMembersInfo(updatedWithdrawalMembersInfo);
      }
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        `Proposal executed successfully`
      );
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        `Proposal execution failed`,
        error.message
      );
    } finally {
      toggleSubmitting(false);
    }
  }, [wallet, clubBasicInfoProposal.treasury, proposalWithMetaData]);

  const executeMEProposal = useCallback(async () => {
    if (
      proposalWithMetaData.metadata.proposalAction === ProposalAction.BuyNft
    ) {
      await executeMagicEdenBuyProposal(
        proposalWithMetaData,
        clubBasicInfoProposal.clubName,
        clubBasicInfoProposal.treasury,
        wallet!
      );
    } else {
      await executeMagicEdenSell(
        clubBasicInfoProposal.clubName,
        proposalWithMetaData,
        clubBasicInfoProposal.treasury,
        wallet!
      );
    }

    await updateProposalAfterExecution();
  }, [wallet, clubBasicInfoProposal, proposalWithMetaData]);

  const updateProposalAfterExecution = useCallback(async () => {
    updateProposalWithMetaData!(proposalWithMetaData.proposalBasicData);
    if (!clubBasicInfoProposal.isDashboardProposal) {
      const updatedTreasury = await getTreasuryForClub(
        clubBasicInfoProposal.clubName
      );
      setTreasury({ ...updatedTreasury });
    }
    if (
      !clubBasicInfoProposal.isDashboardProposal &&
      (proposalWithMetaData.metadata.proposalAction ===
        ProposalAction.SellNft ||
        proposalWithMetaData.metadata.proposalAction === ProposalAction.BuyNft)
    ) {
      const updatedVault = await getClubVault(clubBasicInfoProposal.clubName);
      await refreshSolseaDataAfterListing(
        updatedVault.vaultAddress?.toString()
      );
      setVault({
        nfts: [...updatedVault.nfts],
        vaultAddress: updatedVault.vaultAddress,
      });
      setAllNfts([...updatedVault.nfts]);
    }
  }, [proposalWithMetaData, clubBasicInfoProposal]);

  const executeTradingProposal = useCallback(async () => {
    try {
      toggleSubmitting(true);
      switch (
        proposalWithMetaData.proposalBasicData.proposalMetadata.proposalType
      ) {
        case ProposalTypeForProgram.BuyP2P:
        case ProposalTypeForProgram.SellP2P:
          await executeVaultTreasuryRelatedProposal(
            proposalWithMetaData!,
            wallet!,
            clubBasicInfoProposal.clubName,
            clubBasicInfoProposal.treasury!
          );
          break;
        case ProposalTypeForProgram.BuyNowMagicEden:
          await executeMEProposal();
          break;
        case ProposalTypeForProgram.BuySolsea:
        case ProposalTypeForProgram.SellSolsea:
          await executeSolseaTransaction();
          break;
        case ProposalTypeForProgram.AddSellPermission:
          await executeAddSellPermssion();
          break;
        default:
          throw new Error("Not supported proposal type");
      }
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        "Proposal successfully executed!"
      );
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        `Proposal execution failed`,
        error.message
      );
    } finally {
      toggleSubmitting(false);
      await updateProposalAfterExecution();
    }
  }, [wallet, clubBasicInfoProposal.treasury, proposalWithMetaData]);

  const executeAddSellPermssion = async () => {
    try {
      await executeAddSellPermissionProposal(
        treasury!,
        clubBasicInfo.name,
        wallet!,
        proposalWithMetaData,
        clubBasicInfo.communityMint
      );
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Failed to execute proposal",
        error.message
      );
    }
  };

  const executeTransferProposal = useCallback(async () => {
    try {
      toggleSubmitting(true);

      await executeTransferProposalTx(
        wallet!,
        proposalWithMetaData,
        clubBasicInfoProposal.clubName,
        treasury!
      );
      await updateProposalAfterExecution();
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        "Proposal successfully executed!"
      );
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error.message);
    } finally {
      toggleSubmitting(false);
    }
  }, [wallet, proposalWithMetaData, clubBasicInfoProposal]);

  const executeSolseaTransaction = useCallback(async () => {
    try {
      let solseaEscrowAccount: PublicKey | undefined;
      let seller: PublicKey | undefined;
      const action = proposalWithMetaData.metadata.proposalAction;
      const [nftMint, amount] =
        action === ProposalAction.BuyNft
          ? [
              proposalWithMetaData.metadata.wantedTokenMint,
              proposalWithMetaData.metadata.offeredAmount,
            ]
          : [
              proposalWithMetaData.metadata.offeredTokenMint,
              proposalWithMetaData.metadata.wantedAmount,
            ];
      if (action === ProposalAction.BuyNft) {
        const solseaListingData = await getNftDataFromSolsea(
          nftMint.toString()
        );

        if (!solseaListingData || !solseaListingData.listed)
          throw new Error("NFT unlisted on SolSea");
        if (solseaListingData.price !== amount)
          throw new Error("NFT price changed on SolSea");
        solseaEscrowAccount = new PublicKey(solseaListingData.escrowKey);
        seller = new PublicKey(solseaListingData.sellerKey);
      }
      await executeSolseaProposal(
        proposalWithMetaData.metadata.proposalAction,
        clubBasicInfoProposal.clubName,
        wallet!,
        proposalWithMetaData,
        solseaEscrowAccount,
        seller
      );
      //awaiting 1 sec to let solsea api update listing data
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error: any) {
      throw error;
    }
  }, [proposalWithMetaData, wallet, clubBasicInfoProposal]);

  const executeUpdateRole = useCallback(async () => {
    try {
      await executeUpdateRoleWeight(
        clubBasicInfoProposal.clubName,
        proposalWithMetaData,
        clubBasicInfoProposal.treasury,
        wallet!
      );
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        "Proposal successfully executed!"
      );
      if (clubBasicInfo) {
        const roleConfig = await refreshRoleConfig(
          new PublicKey(clubBasicInfo.address)
        );
        clubBasicInfo.roleConfig = [...roleConfig];
      }
      await updateProposalAfterExecution();
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Failed to update role config",
        error.message
      );
    }
  }, [clubBasicInfoProposal, proposalWithMetaData, wallet]);

  const executeProposal = useCallback(() => {
    switch (proposalWithMetaData.metadata.proposalAction) {
      case ProposalAction.Withdrawal:
        void executeWithdrawal();
        break;
      case ProposalAction.Transfer:
        void executeTransferProposal();
        break;
      case ProposalAction.UpdateGovernanceConfig:
        executeUpdateGovernanceConfig();
        break;
      case ProposalAction.UpdateRoleConfig:
        executeUpdateRole();
        break;
      default:
        void executeTradingProposal();
    }
  }, [proposalWithMetaData]);

  const executeUpdateGovernanceConfig = useCallback(async () => {
    try {
      toggleSubmitting(true);
      await executeUpdateGovConfig(
        clubBasicInfoProposal.clubName,
        proposalWithMetaData,
        clubBasicInfoProposal.treasury,
        wallet!,
        proposalWithMetaData.metadata.governancesToUpdate,
        proposalWithMetaData.metadata.governedAccountsToUpdate
      );
      await updateProposalAfterExecution();
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        "Proposal successfully executed"
      );
      const governances = await getGovernaceAccountsForRealm(
        clubBasicInfo.name
      );
      if (
        proposalWithMetaData.metadata.proposalAction ===
        ProposalAction.AddSellPermission
      ) {
        setTreasury(await getTreasuryForClub(clubBasicInfo.name));
      }
      updateClubBasicInfo({ ...clubBasicInfo, governances });
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Failed to execute proposal",
        error.message
      );
    } finally {
      toggleSubmitting(false);
    }
  }, [clubBasicInfoProposal, proposalWithMetaData, wallet]);

  return (
    <SubmitButton
      disabled={isExecuteDisabled}
      className="proposals-item-actions__form-button"
      onClick={executeProposal}
      submitting={submitting}
    >
      Execute proposal
    </SubmitButton>
  );
};

export default ExecuteButton;
